import { Icon } from '@blueprintjs/core'
import classNames from 'classnames'
import { MenuItem } from '../ui/menu'
import { Tooltip } from '../ui/tooltip'
import { IItemRendererProps, ItemPredicate, Select } from '@blueprintjs/select'
import React, { useEffect, useState } from 'react'
import ReactCountryFlag from 'react-country-flag'
import { ChevronDown } from 'lucide-react'
import { useDispatch } from 'react-redux'
import { observer } from 'mobx-react-lite'
import { setCurrentProfile } from '../../actions/user'
import { useUser } from '../../hooks/useUser'
import { MerchjarLogoMark } from '../../MerchjarLogo'
import { cents } from '../../util/formatters'
import { Marketplace, Marketplaces } from '../../util/marketplaces'
import { Button } from '../ui/button'
import { type EntityId, idKey } from '../../types/constants'

export interface Profile {
    title: string
    id: EntityId
    countryCode: string
    marketplaceId: string
    totalAdSpend30Days: number
    entityId: string
}

// Some profiles have neither a nickname nor an Amazon account name; the menu still needs a label.
export const profileTitle = (profile: {
    nickname?: string | null
    name?: string | null
    profile_id: EntityId
}): string => profile.nickname || profile.name || `Profile ${profile.profile_id}`

const filterProfile: ItemPredicate<Profile> = (query, profile, _index, exactMatch) => {
    const normalizedTitle = profile.title.toLowerCase()
    const normalizedQuery = query.toLowerCase()
    const normalizedEntityId = profile.entityId?.toLowerCase()

    if (exactMatch) {
        return normalizedTitle === normalizedQuery || normalizedEntityId === normalizedQuery
    } else {
        return (
            `${profile.id}. ${normalizedTitle} ${profile.title} ${normalizedEntityId}`.indexOf(
                normalizedQuery,
            ) >= 0
        )
    }
}

const renderProfile = (
    profile: Profile,
    { handleClick, modifiers, query }: IItemRendererProps,
    activeProfile: EntityId | null,
    setManagedProfiles: any,
    sort: string,
    setSort: (v: string) => void,
    screenshotMode: boolean,
) => {
    const handleSort = (sort: string) => {
        setSort(sort)

        switch (sort) {
            case '-spend':
                setManagedProfiles((prev: any[]) => {
                    const first = prev.shift()
                    prev = prev.sort((a, b) => a.totalAdSpend30Days - b.totalAdSpend30Days)
                    prev.unshift(first as any)
                    return [...prev]
                })
                break
            case 'spend':
                setManagedProfiles((prev: any[]) => {
                    const first = prev.shift()
                    prev = prev.sort((a, b) => b.totalAdSpend30Days - a.totalAdSpend30Days)
                    prev.unshift(first as any)
                    return [...prev]
                })
                break
            case '-name':
                setManagedProfiles((prev: any[]) => {
                    const first = prev.shift()
                    prev.sort((a, b) => a.title.localeCompare(b.title))
                    prev.unshift(first as any)
                    return [...prev]
                })
                break
            case 'name':
                setManagedProfiles((prev: any[]) => {
                    const first = prev.shift()
                    prev.sort((a, b) => b.title.localeCompare(a.title))
                    prev.unshift(first as any)
                    return [...prev]
                })
                break
        }
    }

    if (!modifiers.matchesPredicate) {
        return null
    }

    if (profile.id == 0) {
        return (
            <div key="0" className="text-xs p-1 m-0 flex justify-between">
                <p
                    className="p-0 m-0 flex items-center"
                    onClick={() => handleSort(sort == 'name' ? '-name' : 'name')}
                >
                    Profiles
                    <span className="text-xs text-gray-400 ml-1" style={{ fontSize: '0.7em' }}>
                        {sort == '-name' ? ' ▼' : ''}
                        {sort == 'name' ? ' ▲' : ''}
                    </span>
                </p>
                <p
                    className="p-0 m-0 flex items-center"
                    onClick={() => handleSort(sort == 'spend' ? '-spend' : 'spend')}
                >
                    30D Ad Spend
                    <span className="text-xs text-gray-400 ml-1" style={{ fontSize: '0.7em' }}>
                        {sort == '-spend' ? ' ▼' : ''}
                        {sort == 'spend' ? ' ▲' : ''}
                    </span>
                </p>
            </div>
        )
    }

    const shortTitle =
        profile?.title.length > 18 ? profile.title.substring(0, 18) + '...' : profile.title
    const text = (
        <div className="flex pr-4 m-0">
            <span>
                <ReactCountryFlag
                    countryCode={profile.countryCode === 'UK' ? 'gb' : profile.countryCode}
                    svg
                    className="mr-1 text-lg"
                />
                <span className={classNames({ blur: screenshotMode })}>
                    {/* The tooltip portals out of the blurred span. */}
                    {shortTitle !== profile.title && !screenshotMode ? (
                        <Tooltip content={<span>{profile.title}</span>}>{shortTitle}</Tooltip>
                    ) : (
                        shortTitle
                    )}
                </span>
            </span>
        </div>
    )

    return (
        <MenuItem
            className="py-1.5 px-1.5"
            selected={idKey(profile.id) === idKey(activeProfile)}
            disabled={modifiers.disabled}
            key={profile.id}
            label={`${(
                cents(
                    profile.totalAdSpend30Days,
                    Marketplaces[profile.marketplaceId as Marketplace],
                ) ?? ''
            ).toString()}`}
            onClick={handleClick}
            text={text}
        />
    )
}

interface ProfileSelectProps {
    id?: string
    onOpenChange?: (isOpen: boolean) => void
}

export const ProfileSelect: React.FC<ProfileSelectProps> = observer(({ id, onOpenChange }) => {
    const dispatch = useDispatch()
    const { profiles, profile: activeProfileId, screenshotMode } = useUser()
    const [managedProfiles, setManagedProfiles] = useState<Profile[] | undefined>(undefined)
    const [selectedProfile, setSelectedProfile] = React.useState<Profile | undefined>(undefined)
    const [sort, setSort] = useState('-spend')
    // Only observed, so the Select keeps its own query handling.
    const [query, setQuery] = useState('')
    const isNavV2 = id === 'nav-v2-profile-select'

    useEffect(() => {
        // menu header panel
        const menuHeader: Profile = {
            id: 0,
            title: '',
            countryCode: '',
            marketplaceId: '',
            totalAdSpend30Days: 0,
            entityId: '',
        }
        const output: Profile[] = []
        output.push(menuHeader)
        profiles.forEach((profile) => {
            if (profile.managed) {
                const newProfile = {
                    id: profile.profile_id,
                    title: profileTitle(profile),
                    countryCode: profile.country_code,
                    marketplaceId: profile.marketplace_id,
                    totalAdSpend30Days: profile.total_ad_spend_30_days,
                    entityId: profile.entity_id,
                }

                if (idKey(activeProfileId) === idKey(profile.profile_id)) {
                    if (!!selectedProfile && selectedProfile.title !== newProfile.title) {
                        setSelectedProfile(newProfile)
                    }

                    if (!selectedProfile) {
                        setSelectedProfile(newProfile)
                    }
                }

                output.push(newProfile)
            }
        })

        setManagedProfiles(output)
    }, [profiles, activeProfileId])

    const onItemSelect = (profile: Profile) => {
        setSelectedProfile(profile)
        dispatch(setCurrentProfile(profile.id))
    }

    const renderProfileNameWithFlag = () => {
        if (selectedProfile) {
            if (isNavV2) {
                const profileLabel = (
                    <div className="flex min-w-0 items-center gap-2 text-left">
                        <span
                            className={classNames('min-w-0 truncate text-[16px] font-semibold', {
                                blur: screenshotMode,
                            })}
                        >
                            {selectedProfile?.title}
                        </span>
                        <span className="h-1 w-1 shrink-0 rounded-full bg-[#D1D5DB]" />
                        <span className="shrink-0 text-[11px] font-medium uppercase text-[#8A8F98]">
                            {selectedProfile.countryCode}
                        </span>
                    </div>
                )

                return (
                    <div className="flex min-w-0 items-center gap-3">
                        <span className="shrink-0">
                            <MerchjarLogoMark height={25} width={28} />
                        </span>
                        {profileLabel}
                    </div>
                )
            }

            return (
                <div className="flex">
                    <div>
                        <ReactCountryFlag
                            countryCode={
                                selectedProfile.countryCode === 'UK'
                                    ? 'gb'
                                    : selectedProfile.countryCode
                            }
                            svg
                            className="mr-2 text-lg"
                        />
                    </div>
                    <div className={classNames({ blur: screenshotMode })} style={{ paddingTop: 1 }}>
                        {selectedProfile?.title}
                    </div>
                </div>
            )
        }
    }

    return (
        <div className={isNavV2 ? 'h-full w-full' : 'p-3'}>
            <Select<Profile>
                popoverProps={{
                    boundary: 'viewport',
                    position: 'bottom',
                    minimal: false,
                    onInteraction: onOpenChange,
                    popoverClassName: classNames(
                        'rounded-lg profile-select-popover',
                        isNavV2 && 'profile-select-popover-nav-v2',
                    ),
                }}
                className={isNavV2 ? 'h-full rounded-none' : 'rounded-lg'}
                items={managedProfiles ?? []}
                itemPredicate={filterProfile}
                itemRenderer={(profile, props) =>
                    renderProfile(
                        profile,
                        props,
                        activeProfileId,
                        setManagedProfiles,
                        sort,
                        setSort,
                        Boolean(screenshotMode),
                    )
                }
                noResults={<MenuItem disabled={true} text="No results." />}
                onItemSelect={onItemSelect}
                onQueryChange={setQuery}
                fill
                inputProps={{
                    leftIcon: <Icon className="pt-0.5" icon={'search'} color="#3391FF" />,
                    style: {
                        height: 37,
                        borderRadius: '0.5rem',
                    },
                    className: classNames('rounded-lg profile-select-input', {
                        blur: screenshotMode && query !== '',
                    }),
                }}
            >
                {managedProfiles ? (
                    <div>
                        {managedProfiles.length > 0 ? (
                            <div>
                                {isNavV2 ? (
                                    <button
                                        id={id ?? undefined}
                                        type="button"
                                        className="flex h-full min-h-[45px] w-full items-center justify-between gap-2 rounded-none bg-transparent px-0 text-left text-[#242529] focus:outline-none focus-visible:ring-0"
                                    >
                                        <span className="min-w-0">
                                            {renderProfileNameWithFlag()}
                                        </span>
                                        <span className="shrink-0">
                                            <ChevronDown size={14} strokeWidth={1.8} />
                                        </span>
                                    </button>
                                ) : (
                                    <Button
                                        id={id ?? undefined}
                                        style={{ borderRadius: 8 }}
                                        fill
                                        outlined
                                        text={renderProfileNameWithFlag()}
                                        rightIcon="caret-down"
                                    />
                                )}
                            </div>
                        ) : (
                            <div>
                                <Button
                                    id={id ?? undefined}
                                    style={{ borderRadius: 8 }}
                                    fill
                                    outlined
                                    disabled
                                    rightIcon="caret-down"
                                    text="No managed profiles found"
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    <div>
                        <Button
                            id={id ?? undefined}
                            style={{ borderRadius: 8 }}
                            fill
                            outlined
                            disabled
                            rightIcon="caret-down"
                            text="No ad accounts found"
                        />
                    </div>
                )}
            </Select>
        </div>
    )
})
